<script setup lang="ts">
import { TextField } from '@haskoning/vue-component-library'
import { useScenarioFormComposable } from '@/components/scenario-form/composable/useScenarioFormComposable'
import type { ScenarioFormMixinProps } from '@/components/scenario-form/type/type'
import CustomHelpTextToolTip from '@/components/form/CustomHelpTextToolTip.vue'
import CustomCheckBoxField from '@/components/form/CustomCheckBoxField.vue'
import RadioField from '@/components/form/RadioField.vue'

const props = defineProps<ScenarioFormMixinProps>()
const emit = defineEmits(['updateTabCompletion'])

const customIsComplete = (): boolean => {
  const method = currentItem.value?.calculation_method
  return method === 'Shortest route' || method === 'Fastest route'
}

const {
  scenarioStore,
  currentItem,
  isValid,
  formRef,
  disabled,
  isReadOnly,
  hasHelpText,
  isComplete
} = useScenarioFormComposable(props, emit, customIsComplete)
defineExpose({ formRef })

const calculationMethodFields = ['calculation_method']
const intendedOutcome = [
  {
    name: 'is_road_frequency',
    description: `Check this option to include 'Road Frequency' in the calculation.`,
    helpText: `
    Road usage frequency analysis provides insights on how frequently each of the road segments within a network is being used. It shows the sum of all travel patterns that went over this road segment. The higher the frequency, the more people use this road link in their travels from the origin to the destination.<br>
    The analysis can be used to understand mobility patterns of people and the type of roads (main vs. local) they use in order to move around. Analyses like this can inform road safety and explore which road segments are facilitating access to resources or other points of interest amongst population.`,
    title: 'Road usage frequency analysis',
    image: '/RoadFrequency.png'
  },
  {
    name: 'is_accessibility',
    description: `Check this option to include 'Accessibility' in the calculation.`,
    helpText: `
    Accessibility analysis provides insights on the level of accessibility per origin to a chosen destination. It shows the amount of time or distance people need to travel from the origin point to the destination. Longer travel times or distances indicate that these points have less accessibility. The time needed or the distances calculated are plotted on the origin points.<br>
    The analysis can be used to understand which areas have the poorest access, and identify if there are any communities which are at risk of being cut off completely (during a disastrous event)`,
    title: 'Accessibility analysis',
    image: '/Accessibility.png'
  }
]
const subtitle = 'The following parameters are automatically included in the scenario results.'
const fileFields = ['networks', 'origin', 'destinations', 'disruptions']
const choiceInformation: Record<
  string,
  { description: string; helpText: string; message: string }
> = {
  'Fastest route': {
    description:
      'The "fastest route" variant is calculated based on total travel time. Be aware that the tool will prioritize faster routes over shorter routes. To run this variant correctly, make sure that your network dataset contains speed data for each network segment in the column "Minutes". This data needs to be filled by a user upfront. For more informations, please refer to the help text icon.',
    helpText: `The 'fastest route' variant is calculated based on total travel time. Note that the tool will prioritize faster routes over shorter routes. To run this variant correctly, make sure that your network dataset contains speed data for each network segment in the column 'Minutes' (calculated: [Speed_kmh] * [Length] * 60). This data needs to be filled by a user upfront. See documentation for full explanation.`,
    message:
      'The input network should have a column called "Minutes", reflecting the minutes it takes to travel over that road link.'
  },
  'Shortest route': {
    description:
      'The ‘shortest route’ variant is calculated based on total travel distance. Note that the tool will prioritize shorter routes over faster routes.',
    helpText:
      'The ‘shortest route’ variant is calculated based on total travel distance. Note that the tool will prioritize shorter routes over faster routes.',
    message: ''
  }
}

const currentScenarioItem = computed({
  get: () => currentItem.value ?? {},
  set: (value) => {
    currentItem.value = Object.keys(value).length === 0 ? null : (value as typeof currentItem.value)
  }
})

onMounted(() => {
  intendedOutcome.forEach((field) => {
    if (currentItem.value && currentItem.value[field.name] !== true) {
      currentItem.value[field.name] = true
    }
  })
})

watch(isComplete, (value) => {
  emit('updateTabCompletion', props.tab, value)
})
</script>
<template>
  <v-form ref="formRef" v-model="isValid" :disabled="disabled" :readonly="isReadOnly">
    <v-row align="center" no-gutters v-for="field in calculationMethodFields" :key="field">
      <v-col cols="20" md="8" lg="4" class="pt-0">
        <RadioField
          v-if="scenarioStore.fields?.[field]"
          :field="scenarioStore.fields[field]"
          v-model="currentScenarioItem"
          :descriptions="
            Object.entries(choiceInformation).map(([key, value]) => ({
              [key]: {
                description: value.description,
                helpText: value.helpText,
                message: value.message
              }
            }))
          "
          :title="scenarioStore.fields[field]?.label"
          :readonly="isReadOnly"
          :subtitle="scenarioStore.fields[field]?.help_text"
          @update:modelValue="
            currentItem && scenarioStore.updateScenario(currentItem.id, currentItem, fileFields)
          "
          class="px-2 v-input"
        />
      </v-col>
    </v-row>
    <v-card-title
      class="header-font d-flex justify-space-between align-center"
      :style="{ marginLeft: '-10px', marginTop: '-5px', marginBottom: '-5px' }"
    >
      Automatically provided results
    </v-card-title>
    <div class="subtitle-text">{{ subtitle }}</div>
    <v-row
      align="center"
      no-gutters
      v-for="field in intendedOutcome"
      :key="field.name"
      class="mb-2"
      :class="field.name === 'is_accessibility' ? 'mb-4' : ''"
    >
      <v-col cols="12" md="10" lg="7" class="d-flex align-center" style="margin-left: 1px">
        <CustomHelpTextToolTip
          :text="field.helpText || ''"
          :help-text="field.description"
          :title="field.title"
          :image="field.image"
        />
        <CustomCheckBoxField
          v-if="scenarioStore.fields?.[field.name]"
          :field="scenarioStore.fields[field.name]"
          v-model="currentScenarioItem"
          :no-help-text="true"
          :readonly="true"
          :disabled="true"
          position="back"
          :showCheckBox="true"
          class="px-2 v-input"
        />
      </v-col>
    </v-row>
    <v-card-title
      class="header-font d-flex"
      :style="{ marginLeft: '-10px', marginTop: '-5px', marginBottom: '-5px', fontSize: '1rem' }"
    >
      Distance tolerance to network
      <span style="margin-right: 5px"></span>
      <div>
        <CustomHelpTextToolTip
          text="Some origin points may not be directly adjacent to the road network and could be excluded during route generation. To mitigate this, the tool allows snapping origin and destination points to the network within a user-defined distance. This process simulates walking from the origin point to the nearest road (in a straight line, as the crow flies) before transitioning to the chosen mode of transport. The walking speed used for this simulation is 8 km/h."
        />
      </div>
    </v-card-title>
    <v-row align="center" no-gutters>
      <v-col cols="12" md="8" lg="4" class="d-flex align-center ml-1" style="margin-bottom: 10px">
        <CustomHelpTextToolTip
          v-if="hasHelpText('acceptance_distance')"
          :text="scenarioStore.fields?.['acceptance_distance']?.help_text || ''"
        />
        <span :class="hasHelpText('acceptance_distance') ? 'px-2' : 'px-0'">
          {{ scenarioStore.fields?.['acceptance_distance']?.label }}
        </span>
      </v-col>
      <v-col cols="12" md="8" lg="4" class="pt-0 ml-15">
        <TextField
          v-if="scenarioStore.fields?.['acceptance_distance']"
          :field="scenarioStore.fields['acceptance_distance']"
          label="Acceptable distance"
          v-model="currentScenarioItem"
          :no-help-text="true"
          :no-label="false"
          :readonly="isReadOnly"
          @update="
            currentItem && scenarioStore.updateScenario(currentItem.id, currentItem, fileFields)
          "
        />
      </v-col>
    </v-row>
    <v-row v-if="currentItem?.calculation_method" class="mt-5">
      <v-col cols="12">
        <div class="note-box">
          <div class="note-title">Note</div>
          <div class="note-text">
            Note that your projects will be saved for 2 months. The results will afterwards be
            deleted, to regain storage space for new projects. Your input data will remain saved,
            so, if needed, the simulation can be run again
          </div>
        </div>
      </v-col>
    </v-row>
  </v-form>
</template>

<style scoped>
.v-input :deep(.v-input__control) {
  margin-left: -10px;
}

.v-input :deep(.v-input__details) {
  display: none;
}
.custom-message {
  margin-top: 4px;
  font-size: 0.875rem;
  font-style: italic;
  color: inherit;
}
.bigger-subtitle {
  font-size: 1.125rem;
  font-weight: 500;
  color: black;
}
.v-card {
  background-color: #f9f9f9;
  border-color: var(--v-primary-lighten2);
}

.v-card-title {
  color: var(--v-primary-darken1);
}

.note-box {
  background: #f9f9f9;
  border-radius: 12px;
  padding: 18px 24px;
  margin-bottom: 12px;
  border: 1px solid #ededed;
  display: flex;
  flex-direction: column;
}
.note-title {
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 6px;
  color: #222;
}
.note-text {
  font-size: 0.95rem;
  color: inherit; /* Use default text color */
}
::v-deep(.mdi-information-slab-circle-outline.mdi.v-icon.notranslate.v-theme--haskoningLight) {
  font-size: 1.5rem;
}
.subtitle-text {
  font-size: 0.9rem;
  color: gray;
  margin-top: -8px;
  white-space: nowrap;
  margin-left: 5px;
}
</style>
